/**
 * Gedeelde klassen voor schermen in de ledger-stijl (inlog, Instellingen →
 * Workspace). Ze lezen de `--ld-*`-tokens uit `styles/globals.css`, dus ze
 * werken alleen binnen een element met de klasse `ledger` en volgen daar
 * vanzelf licht en donker.
 */

const focus =
  "focus:border-(--ld-accent) focus:outline-none focus:ring-1 focus:ring-(--ld-accent) focus-visible:outline-none";

export const ld = {
  /** Titel van een weergave binnen de modal. */
  viewTitle: "font-semibold text-lg tracking-[-0.01em]",
  viewIntro: "mt-1 max-w-[64ch] text-pretty text-(--ld-muted) text-sm leading-6",
  sectionTitle: "font-medium text-[15px]",
  sectionIntro: "mt-1 text-pretty text-(--ld-muted) text-sm leading-6",
  panel: "rounded-lg border border-(--ld-border) bg-(--ld-surface) p-4",
  well: "rounded-lg bg-(--ld-raised) p-4",
  list: "divide-y divide-(--ld-rule) rounded-lg border border-(--ld-border) bg-(--ld-surface)",
  label: "block font-medium text-sm",
  hint: "mt-1.5 text-(--ld-muted) text-[13px] leading-5",
  input: `mt-2 h-9 w-full rounded-md border border-(--ld-control) bg-(--ld-surface) px-3 text-(--ld-text) text-sm placeholder:text-(--ld-muted) transition-colors disabled:opacity-60 ${focus}`,
  /** Zonder marge en breedte, voor een veld in een rij. */
  inputInline: `h-9 rounded-md border border-(--ld-control) bg-(--ld-surface) px-3 text-(--ld-text) text-sm placeholder:text-(--ld-muted) transition-colors disabled:opacity-60 ${focus}`,
  primary:
    "inline-flex h-9 shrink-0 items-center justify-center rounded-md bg-(--ld-primary) px-3.5 font-medium text-(--ld-primary-ink) text-sm transition-colors hover:bg-(--ld-primary-hover) active:translate-y-px disabled:translate-y-0 disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)",
  secondary:
    "inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-(--ld-control) bg-(--ld-surface) px-3.5 font-medium text-(--ld-text) text-sm transition-colors hover:bg-(--ld-raised) active:translate-y-px disabled:translate-y-0 disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)",
  /** Kleinere variant voor een actie in een rij. */
  secondarySm:
    "inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-(--ld-control) bg-(--ld-surface) px-3 font-medium text-(--ld-text) text-[13px] transition-colors hover:bg-(--ld-raised) active:translate-y-px disabled:translate-y-0 disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)",
  danger:
    "inline-flex h-9 shrink-0 items-center justify-center rounded-md border border-(--ld-control) bg-(--ld-surface) px-3.5 font-medium text-(--ld-negative) text-sm transition-colors hover:bg-(--ld-negative-bg) active:translate-y-px disabled:translate-y-0 disabled:text-(--ld-disabled-ink) disabled:hover:bg-(--ld-surface)",
  dangerFilled:
    "inline-flex h-9 shrink-0 items-center justify-center rounded-md bg-(--ld-negative) px-3.5 font-medium text-sm text-white transition-colors hover:opacity-90 active:translate-y-px disabled:translate-y-0 disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)",
  textButton:
    "rounded-sm font-medium text-(--ld-accent) text-sm underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-(--ld-disabled-ink) disabled:no-underline",
  textDanger:
    "rounded-sm font-medium text-(--ld-negative) text-[13px] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-(--ld-disabled-ink) disabled:no-underline",
  error: "rounded-md bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm",
  success: "rounded-md bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm",
  notice: "rounded-md bg-(--ld-notice-bg) px-3 py-2 text-(--ld-notice-ink) text-sm",
  muted: "text-(--ld-muted) text-sm",
  mono: "font-mono text-(--ld-muted) text-xs",
} as const;

const pillBase =
  "inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded px-1.5 font-medium text-xs";

export const pill = {
  positive: `${pillBase} bg-(--ld-positive-bg) text-(--ld-positive-ink)`,
  negative: `${pillBase} bg-(--ld-negative-bg) text-(--ld-negative-ink)`,
  notice: `${pillBase} bg-(--ld-notice-bg) text-(--ld-notice-ink)`,
  neutral: `${pillBase} bg-(--ld-active) text-(--ld-text)`,
} as const;
